import PageLayout from "@/components/PageLayout";
import PageHeader from "@/components/PageHeader";
import { motion } from "framer-motion";
import { useEffect, useRef } from "react";
import SEO from "@/components/SEO";

const ptPackages = [
  { sessions: "3x 1h", price: "205€" },
  { sessions: "5x 1h", price: "305€" },
  { sessions: "8x 1h", price: "460€" },
];

const programs = [
  { name: "1-jakoinen kuntosaliohjelma", price: "47€" },
  { name: "2-jakoinen kuntosaliohjelma", price: "55€" },
  { name: "3-jakoinen kuntosaliohjelma", price: "59€" },
];

export default function ValmennusPage() {
  const webstoreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://avoinna24.fi/js/a24.iframe.js";
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const scrollToWebstore = () => {
    webstoreRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <PageLayout>
      <SEO title="Personal Training & Valmennus — Kuntosali Takomo" description="Henkilökohtainen valmennus Jokelassa ja Kellokoskella. Personal training, saliohjelmat ja ruokavalio-ohjaus. Räätälöity juuri sinulle." path="/valmennus" />
      <PageHeader title="Valmennus" subtitle="Laaditaan juuri sinulle sopiva valmennus tarpeidesi mukaan." />

      <section className="section-padding">
        <div className="container mx-auto max-w-5xl space-y-16">

          {/* Personal Training */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="font-display text-2xl md:text-3xl font-bold mb-2 text-foreground">Personal Training</h2>
            <div className="divider-primary mb-6" />
            <p className="text-muted-foreground mb-8 max-w-2xl">
              Olipa tavoitteesi elämäntaparemontti, lihasmassan kasvatus tai rasvanpoltto — räätälöimme valmennuksen sinulle. Sisältää henkilökohtaisen saliohjelman ja ruokavalion. Tapaamiset vähintään 2 viikon välein.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {ptPackages.map((pkg, i) => (
                <div key={i} className="bg-card-premium rounded-lg p-6 text-center">
                  <p className="font-display text-lg text-foreground font-semibold mb-2">{pkg.sessions}</p>
                  <p className="text-3xl font-display font-bold text-primary mb-4">{pkg.price}</p>
                  <button onClick={scrollToWebstore} className="btn-outline-premium inline-flex items-center gap-2 px-6 py-2 rounded text-sm cursor-pointer">
                    Osta
                  </button>
                </div>
              ))}
            </div>
            <p className="text-primary text-sm mt-4 font-semibold">Yhdessä ystävän kanssa kaikki hinnat -30% / henkilö!</p>
          </motion.div>

          {/* Kuntosaliohjelmat */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="font-display text-2xl md:text-3xl font-bold mb-2 text-foreground">Kuntosaliohjelma + Tekniikkaopastus</h2>
            <div className="divider-primary mb-6" />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {programs.map((p, i) => (
                <div key={i} className="bg-card-premium rounded-lg p-6 text-center">
                  <p className="text-foreground font-display mb-2">{p.name}</p>
                  <p className="text-2xl font-display font-bold text-primary mb-4">{p.price}</p>
                  <button onClick={scrollToWebstore} className="btn-outline-premium inline-flex items-center gap-2 px-6 py-2 rounded text-sm cursor-pointer">
                    Osta
                  </button>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Online valmennus */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="font-display text-2xl md:text-3xl font-bold mb-2 text-foreground">Onlinevalmennus</h2>
            <div className="divider-primary mb-6" />
            <div className="bg-card-premium rounded-lg p-8">
              <ul className="space-y-3 text-foreground/80 text-sm mb-6">
                <li>• Yksilölliset progressiivisesti kehittyvät treeniohjelmat ja ruokavaliot suoraan puhelinsovellukseen</li>
                <li>• 24/7 valmentajan tuki</li>
                <li>• Kerran viikossa raportointi — painot, treenit, fiilikset, muokkaukset</li>
              </ul>
              <div className="flex items-end gap-2 mb-4">
                <span className="text-3xl font-display font-bold text-primary">120€</span>
                <span className="text-muted-foreground text-sm">/kk, min. 3kk</span>
              </div>
              <button onClick={scrollToWebstore} className="btn-primary-premium inline-flex items-center gap-2 px-8 py-3 rounded text-sm cursor-pointer">
                Osta
              </button>
            </div>
          </motion.div>

          {/* Pienryhmät */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="bg-card-premium rounded-lg p-8">
              <h3 className="font-display text-xl font-semibold text-foreground mb-2">Pienryhmävalmennus</h3>
              <div className="divider-primary mb-4" />
              <ul className="space-y-2 text-foreground/80 text-sm mb-4">
                <li>• 4 henkilön ryhmät</li>
                <li>• Yhteistreenit kerran viikossa</li>
                <li>• Ohjeet ruokailuihin ja omatoimisiin treeneihin</li>
                <li>• Kesto 8 viikkoa</li>
              </ul>
              <p className="text-2xl font-display font-bold text-primary">290€</p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="bg-card-premium rounded-lg p-8">
              <h3 className="font-display text-xl font-semibold text-foreground mb-2">Eläkeläisryhmät</h3>
              <div className="divider-primary mb-4" />
              <ul className="space-y-2 text-foreground/80 text-sm mb-4">
                <li>• Jokelassa & Kellokoskella</li>
                <li>• 8–10 henkilön ryhmät</li>
                <li>• Yhteistreenit kerran viikossa</li>
                <li>• Kesto 6 viikkoa</li>
              </ul>
              <p className="text-2xl font-display font-bold text-primary">55€</p>
            </motion.div>
          </div>

          <p className="text-center text-muted-foreground text-sm">
            Ota yhteyttä: <a href="mailto:info@kuntotakomo.fi" className="text-primary hover:underline">info@kuntotakomo.fi</a> | <a href="tel:0449980593" className="text-primary hover:underline">044 9980593</a> | <a href="tel:0443394158" className="text-primary hover:underline">044 3394158</a>
          </p>

          {/* Embedded Webstore */}
          <div ref={webstoreRef} id="webstore">
            <h3 className="font-display text-xl mb-6 text-foreground text-center">Verkkokauppa</h3>
            <div className="bg-gray-100 rounded-lg p-4">
              <iframe
                src="https://avoinna24.fi/takomo/webstore"
                allowTransparency={true}
                className="a24-iframe"
                frameBorder="0"
                scrolling="no"
                style={{ width: "100%", minHeight: "800px" }}
              />
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
